import type { AppData } from "@/types";

// Respaldo y restauración en JSON, adaptado a la arquitectura de datos de ATDD
// (los datos viven en AppData con nombres en camelCase, no en un modelo con
// user_id). El "registro de tablas" describe cada categoría respaldable: su
// etiqueta, su ícono y los campos que forman la firma para deduplicar.

export type BackupKey =
  | "products"
  | "clients"
  | "inventory"
  | "sales"
  | "expenses"
  | "receivables"
  | "loans"
  | "promotions"
  | "encargos"
  | "advisorMovements"
  | "restockItems"
  | "stockEntries"
  | "settings";

export interface BackupTableDef {
  key: BackupKey;
  label: string;
  icon: string;
  kind: "array" | "settings";
  // Campos que se concatenan para detectar duplicados al restaurar.
  signature: string[];
  // Campos obligatorios; si falta alguno, la fila se omite (skipped).
  required?: string[];
}

export const BACKUP_TABLES: BackupTableDef[] = [
  {
    key: "products",
    label: "Catálogo",
    icon: "ri-price-tag-3-line",
    kind: "array",
    signature: ["sku"],
    required: ["sku", "name"],
  },
  {
    key: "clients",
    label: "Clientes",
    icon: "ri-user-line",
    kind: "array",
    signature: ["cedula", "name", "phone"],
    required: ["name"],
  },
  {
    key: "inventory",
    label: "Inventario",
    icon: "ri-archive-2-line",
    kind: "array",
    signature: ["productId"],
    required: ["productId"],
  },
  {
    key: "sales",
    label: "Ventas",
    icon: "ri-shopping-bag-3-line",
    kind: "array",
    signature: ["date", "productName", "clientName", "qty", "total"],
    required: ["productName"],
  },
  {
    key: "expenses",
    label: "Gastos",
    icon: "ri-wallet-3-line",
    kind: "array",
    signature: ["date", "concept", "amount", "wallet"],
    required: ["concept"],
  },
  {
    key: "receivables",
    label: "Cartera",
    icon: "ri-hand-coin-line",
    kind: "array",
    signature: ["date", "clientName", "productName", "initialValue"],
    required: ["clientName"],
  },
  {
    key: "loans",
    label: "Préstamos",
    icon: "ri-exchange-dollar-line",
    kind: "array",
    signature: ["date", "concept", "value", "type"],
    required: ["concept"],
  },
  {
    key: "promotions",
    label: "Promociones",
    icon: "ri-percent-line",
    kind: "array",
    signature: ["title", "discountPct", "startDate", "endDate"],
    required: ["title"],
  },
  {
    key: "encargos",
    label: "Encargos",
    icon: "ri-file-list-3-line",
    kind: "array",
    signature: ["date", "advisorName", "productName", "qty"],
    required: ["productName"],
  },
  {
    key: "advisorMovements",
    label: "Mov. asesor",
    icon: "ri-swap-box-line",
    kind: "array",
    signature: ["date", "advisorId", "productId", "kind", "qty", "amount"],
  },
  {
    key: "restockItems",
    label: "Reposiciones",
    icon: "ri-refresh-line",
    kind: "array",
    signature: ["createdAt", "advisorId", "productId", "qty"],
  },
  {
    key: "stockEntries",
    label: "Puntos (PV)",
    icon: "ri-star-line",
    kind: "array",
    signature: ["date", "productName", "qty", "pvTotal", "source"],
  },
  {
    key: "settings",
    label: "Configuración",
    icon: "ri-settings-5-line",
    kind: "settings",
    signature: [],
  },
];

export const BACKUP_APP = "ATDD";
export const BACKUP_TYPE = "attd-backup";
export const BACKUP_VERSION = 1;

export interface BackupPayload {
  app: string;
  type: string;
  version: number;
  exportedAt: string;
  counts: Record<string, number>;
  data: Record<string, unknown>;
}

export interface AvailableTable {
  key: BackupKey;
  label: string;
  icon: string;
  count: number;
}

export interface PreviewRow {
  key: BackupKey;
  label: string;
  newCount: number;
  duplicates: number;
  skipped: number;
}

export interface RestoreSummaryRow {
  key: BackupKey;
  label: string;
  inserted: number;
  duplicates: number;
  skipped: number;
  error?: string;
}

export interface RestoreSummary {
  rows: RestoreSummaryRow[];
  inserted: number;
  duplicates: number;
  skipped: number;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

// --------------------------------------------- Construcción del respaldo
export const buildBackupPayload = (data: AppData): BackupPayload => {
  const out: Record<string, unknown> = {};
  const counts: Record<string, number> = {};
  for (const def of BACKUP_TABLES) {
    const value = (data as unknown as Record<string, unknown>)[def.key];
    if (def.kind === "settings") {
      out[def.key] = value ?? null;
      counts[def.key] = value ? 1 : 0;
    } else {
      const arr = Array.isArray(value) ? value : [];
      out[def.key] = arr;
      counts[def.key] = arr.length;
    }
  }
  return {
    app: BACKUP_APP,
    type: BACKUP_TYPE,
    version: BACKUP_VERSION,
    exportedAt: new Date().toISOString(),
    counts,
    data: out,
  };
};

export const backupFileName = (): string =>
  `ATDD_respaldo_${new Date().toISOString().slice(0, 10)}.json`;

// Descarga un objeto como archivo .json (application/octet-stream fuerza la
// descarga en vez de abrirlo en una pestaña).
export const downloadJson = (payload: unknown, filename: string): void => {
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: "application/octet-stream",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
};

export const exportBackupJson = (data: AppData): BackupPayload => {
  const payload = buildBackupPayload(data);
  downloadJson(payload, backupFileName());
  return payload;
};

// --------------------------------------------------- Lectura del archivo
export const parseBackupJson = (text: string): BackupPayload => {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new Error("El archivo no es un JSON válido.");
  }
  if (!raw || typeof raw !== "object") {
    throw new Error("El archivo no tiene un formato de respaldo reconocible.");
  }
  const obj = raw as Record<string, unknown>;
  // Acepta el formato nuevo ({ data: {...} }) y también un respaldo viejo que
  // volcaba AppData en la raíz.
  const dataRaw = asRecord(obj.data) && Object.keys(asRecord(obj.data)).length > 0
    ? asRecord(obj.data)
    : obj;

  const data: Record<string, unknown> = {};
  const counts: Record<string, number> = {};
  for (const def of BACKUP_TABLES) {
    const value = dataRaw[def.key];
    if (def.kind === "settings") {
      if (value && typeof value === "object") {
        data[def.key] = value;
        counts[def.key] = 1;
      }
    } else if (Array.isArray(value)) {
      data[def.key] = value;
      counts[def.key] = value.length;
    }
  }
  return {
    app: typeof obj.app === "string" ? obj.app : BACKUP_APP,
    type: typeof obj.type === "string" ? obj.type : BACKUP_TYPE,
    version: typeof obj.version === "number" ? obj.version : BACKUP_VERSION,
    exportedAt: typeof obj.exportedAt === "string" ? obj.exportedAt : "",
    counts,
    data,
  };
};

// Solo las categorías que REALMENTE vienen en el archivo.
export const listAvailableTables = (payload: BackupPayload): AvailableTable[] =>
  BACKUP_TABLES.map((def) => ({
    key: def.key,
    label: def.label,
    icon: def.icon,
    count: payload.counts[def.key] ?? 0,
  })).filter((t) => t.count > 0);

// ------------------------------------------------------------- Utilidades
export const buildSignature = (item: Record<string, unknown>, fields: string[]): string =>
  fields
    .map((field) => String(item?.[field] ?? "").trim().toLowerCase())
    .join("\u0001");

export const isRequiredFilled = (
  item: Record<string, unknown>,
  required: string[] = []
): boolean => required.every((field) => String(item?.[field] ?? "").trim() !== "");

// Vista previa (sin escribir nada): cuántas filas se agregarían, cuántas ya
// existen y cuántas se omiten por inválidas.
export const previewRestore = (
  payload: BackupPayload,
  data: AppData,
  selection: BackupKey[]
): PreviewRow[] => {
  const rows: PreviewRow[] = [];
  for (const def of BACKUP_TABLES) {
    if (!selection.includes(def.key)) continue;

    if (def.kind === "settings") {
      const incoming = payload.data[def.key];
      const existing = (data as unknown as Record<string, unknown>)[def.key];
      const same = JSON.stringify(incoming) === JSON.stringify(existing);
      rows.push({
        key: def.key,
        label: def.label,
        newCount: same ? 0 : 1,
        duplicates: same ? 1 : 0,
        skipped: 0,
      });
      continue;
    }

    const existing =
      ((data as unknown as Record<string, unknown>)[def.key] as Record<string, unknown>[]) ?? [];
    const signatures = new Set(existing.map((item) => buildSignature(item, def.signature)));
    const incoming = (payload.data[def.key] as Record<string, unknown>[]) ?? [];

    let newCount = 0;
    let duplicates = 0;
    let skipped = 0;
    for (const item of incoming) {
      if (!isRequiredFilled(item, def.required)) {
        skipped += 1;
        continue;
      }
      const signature = buildSignature(item, def.signature);
      if (signatures.has(signature)) {
        duplicates += 1;
        continue;
      }
      signatures.add(signature);
      newCount += 1;
    }
    rows.push({ key: def.key, label: def.label, newCount, duplicates, skipped });
  }
  return rows;
};